import { adToBs, isValidBSDate } from '../utils/converter';
import { HOLIDAY_DATA, Holiday } from '../data/holidays';

type BSDate = { year: number; month: number; day: number };

function resolveBSDate(date: Date | string | number | BSDate): BSDate {
  if (date instanceof Date) {
    return adToBs(date);
  }
  if (typeof date === 'number') {
    return adToBs(date);
  }
  if (typeof date === 'string') {
    const parts = date.split(/[-/.]/);
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const day = parseInt(parts[2], 10);
      if (isValidBSDate(year, month, day)) {
        return { year, month, day };
      }
    }
    const adDate = new Date(date);
    if (!isNaN(adDate.getTime())) {
      return adToBs(adDate);
    }
    throw new Error(`Invalid date input: "${date}"`);
  }
  if (typeof date === 'object' && date !== null && 'year' in date && 'month' in date && 'day' in date) {
    if (!isValidBSDate(date.year, date.month, date.day)) {
      throw new Error(`Invalid BS date: ${date.year}-${date.month}-${date.day}`);
    }
    return date;
  }
  throw new Error('Invalid date type');
}

/**
 * Returns all holidays for a given BS year.
 * @param year Bikram Sambat year (e.g., 2080)
 */
export function getHolidays(year: number): Holiday[] {
  const holidays = HOLIDAY_DATA[String(year)];
  return holidays ? [...holidays] : [];
}

/**
 * Checks if a given date is a holiday.
 * @param dateInput Date in BS or AD format
 */
export function isHoliday(dateInput: Date | string | number | BSDate): boolean {
  const bsDate = resolveBSDate(dateInput);
  const holidays = getHolidays(bsDate.year);
  const dateStr = `${bsDate.year}-${String(bsDate.month).padStart(2, '0')}-${String(bsDate.day).padStart(2, '0')}`;
  return holidays.some(h => h.date === dateStr);
}

/**
 * Returns the holiday details for a given date, or null if it's not a holiday.
 * @param dateInput Date in BS or AD format
 */
export function getHolidayEvent(dateInput: Date | string | number | BSDate): Holiday | null {
  const bsDate = resolveBSDate(dateInput);
  const holidays = getHolidays(bsDate.year);
  const dateStr = `${bsDate.year}-${String(bsDate.month).padStart(2, '0')}-${String(bsDate.day).padStart(2, '0')}`;
  return holidays.find(h => h.date === dateStr) || null;
}
